import React, { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useToast } from '../hooks/useToast'
import { Building2, Plus, Trash2 } from 'lucide-react'

// Admin tab for Companies House Watch: create/disable reporting organisations
// (contexts) and control which portal user sees which context. A user sees a
// context in the app if and only if they have a chw_org_members row — that is
// the entire direction mechanism. Only the superadmin can reach this
// component (AdminPanel gates on the admin UUID) and RLS enforces the same on
// the tables.

const FONT = 'Lexend Deca, sans-serif'

interface Org {
  id: string
  org_key: string
  display_name: string
  is_active: boolean
  settings: Record<string, unknown> | null
}

interface PortalUser {
  id: string
  email: string
  full_name: string | null
}

interface Membership {
  org_id: string
  user_id: string
}

const inputStyle: React.CSSProperties = {
  padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db',
  fontSize: '13px', fontFamily: FONT, background: 'white', outline: 'none',
}
const btnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
  background: '#2563eb', color: 'white', fontWeight: 600, borderRadius: '8px',
  border: 'none', cursor: 'pointer', fontSize: '13px', fontFamily: FONT,
}

export const CHWatchAdmin: React.FC = () => {
  const { showToast } = useToast()
  const [orgs, setOrgs] = useState<Org[]>([])
  const [users, setUsers] = useState<PortalUser[]>([])
  const [members, setMembers] = useState<Membership[]>([])
  const [loading, setLoading] = useState(true)
  const [newKey, setNewKey] = useState('')
  const [newName, setNewName] = useState('')
  const [creating, setCreating] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [orgRes, memberRes, userRes] = await Promise.all([
        supabase.from('chw_orgs').select('id, org_key, display_name, is_active, settings').order('org_key'),
        supabase.from('chw_org_members').select('org_id, user_id'),
        supabase.rpc('get_all_users_with_profiles'),
      ])
      if (orgRes.error) throw orgRes.error
      if (memberRes.error) throw memberRes.error
      if (userRes.error) throw userRes.error
      setOrgs((orgRes.data || []) as Org[])
      setMembers((memberRes.data || []) as Membership[])
      setUsers(((userRes.data || []) as any[]).map(u => ({
        id: u.id, email: u.email, full_name: u.full_name,
      })))
    } catch (e) {
      showToast('error', 'Failed to load Companies House Watch admin data')
    } finally {
      setLoading(false)
    }
  }, [showToast])

  useEffect(() => { load() }, [load])

  const createOrg = async () => {
    const key = newKey.trim().toLowerCase()
    if (!/^[a-z0-9][a-z0-9-]*$/.test(key)) {
      showToast('error', 'Org key must be lowercase letters, digits and hyphens')
      return
    }
    setCreating(true)
    try {
      const { error } = await supabase.from('chw_orgs').insert({
        org_key: key, display_name: newName.trim() || key,
      })
      if (error) throw error
      showToast('success', `Organisation ${key} created. Remember: the worker's config.json needs a matching portal entry (HubSpot token) before digests can run.`)
      setNewKey(''); setNewName('')
      load()
    } catch (e: any) {
      showToast('error', e?.message || 'Failed to create organisation')
    } finally {
      setCreating(false)
    }
  }

  const toggleActive = async (org: Org) => {
    const { error } = await supabase.from('chw_orgs')
      .update({ is_active: !org.is_active }).eq('id', org.id)
    if (error) showToast('error', 'Failed to update organisation')
    else load()
  }

  const deleteOrg = async (org: Org) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(
      `Delete ${org.display_name}? All its reports, runs and memberships will be removed.`)) return
    const { error } = await supabase.from('chw_orgs').delete().eq('id', org.id)
    if (error) showToast('error', 'Failed to delete organisation')
    else load()
  }

  const isMember = (orgId: string, userId: string) =>
    members.some(m => m.org_id === orgId && m.user_id === userId)

  const toggleMembership = async (org: Org, u: PortalUser) => {
    if (isMember(org.id, u.id)) {
      const { error } = await supabase.from('chw_org_members')
        .delete().eq('org_id', org.id).eq('user_id', u.id)
      if (error) { showToast('error', 'Failed to remove access'); return }
      setMembers(prev => prev.filter(m => !(m.org_id === org.id && m.user_id === u.id)))
    } else {
      const { data: auth } = await supabase.auth.getUser()
      const { error } = await supabase.from('chw_org_members')
        .insert({ org_id: org.id, user_id: u.id, added_by: auth.user?.id })
      if (error) { showToast('error', 'Failed to grant access'); return }
      setMembers(prev => [...prev, { org_id: org.id, user_id: u.id }])
    }
  }

  if (loading) {
    return <div style={{ padding: 24, fontFamily: FONT, color: '#6b7280' }}>Loading…</div>
  }

  return (
    <div style={{ fontFamily: FONT }}>
      <div style={{
        background: 'white', border: '1px solid #e5e7eb', borderRadius: 12,
        padding: '16px 20px', marginBottom: 18,
      }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: '#111827', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Building2 size={16} /> Reporting organisations
        </div>
        <p style={{ fontSize: 12.5, color: '#6b7280', margin: '0 0 12px' }}>
          One organisation = one reporting context (one HubSpot portal + one ICP).
          Users assigned below see that organisation's reports and settings in the
          Companies House Watch app — and nothing else. The org key must match a
          portal key in the worker's config.json.
        </p>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <input
            placeholder="org-key (e.g. enduro-finance)" value={newKey}
            onChange={e => setNewKey(e.target.value)} style={{ ...inputStyle, width: 220 }}
          />
          <input
            placeholder="Display name" value={newName}
            onChange={e => setNewName(e.target.value)} style={{ ...inputStyle, width: 220 }}
          />
          <button onClick={createOrg} disabled={creating} style={btnStyle}>
            <Plus size={14} /> Add organisation
          </button>
        </div>
        {orgs.map(org => (
          <div key={org.id} style={{
            display: 'flex', gap: 10, alignItems: 'center', padding: '8px 0',
            borderTop: '1px solid #f3f4f6', fontSize: 13,
          }}>
            <span style={{ fontWeight: 600, color: '#111827', width: 190 }}>{org.display_name}</span>
            <code style={{ fontSize: 12, color: '#6b7280', width: 170 }}>{org.org_key}</code>
            <span style={{
              padding: '2px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600,
              background: org.is_active ? '#ecfdf5' : '#f3f4f6',
              color: org.is_active ? '#15803d' : '#6b7280',
            }}>
              {org.is_active ? 'active' : 'disabled'}
            </span>
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              {members.filter(m => m.org_id === org.id).length} user(s)
            </span>
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button
                onClick={() => toggleActive(org)}
                style={{ ...inputStyle, cursor: 'pointer', padding: '5px 12px' }}
              >
                {org.is_active ? 'Disable' : 'Enable'}
              </button>
              <button
                onClick={() => deleteOrg(org)}
                style={{ ...inputStyle, cursor: 'pointer', padding: '5px 10px', color: '#b91c1c', borderColor: '#fecaca' }}
                aria-label={`Delete ${org.display_name}`}
              >
                <Trash2 size={14} />
              </button>
            </span>
          </div>
        ))}
        {!orgs.length && (
          <div style={{ fontSize: 13, color: '#6b7280', paddingTop: 8 }}>
            No organisations yet — add the first one above.
          </div>
        )}
      </div>

      {orgs.length > 0 && (
        <div style={{
          background: 'white', border: '1px solid #e5e7eb', borderRadius: 12,
          padding: '16px 20px', overflowX: 'auto',
        }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#111827', marginBottom: 10 }}>
            Who sees which context
          </div>
          <table style={{ borderCollapse: 'collapse', fontSize: 13, minWidth: 480 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '6px 10px', color: '#6b7280', fontWeight: 600, fontSize: 12 }}>User</th>
                {orgs.map(o => (
                  <th key={o.id} style={{ padding: '6px 10px', color: '#6b7280', fontWeight: 600, fontSize: 12 }}>
                    {o.display_name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '6px 10px' }}>
                    <span style={{ fontWeight: 500, color: '#111827' }}>{u.full_name || u.email}</span>
                    {u.full_name && (
                      <span style={{ color: '#9ca3af', fontSize: 12 }}> · {u.email}</span>
                    )}
                  </td>
                  {orgs.map(o => (
                    <td key={o.id} style={{ textAlign: 'center', padding: '6px 10px' }}>
                      <input
                        type="checkbox"
                        checked={isMember(o.id, u.id)}
                        onChange={() => toggleMembership(o, u)}
                        aria-label={`${u.email} access to ${o.display_name}`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
